import { FC, useEffect, useState } from "react";
import { PluginWrapper } from "../../../components/plugins";
import styles from '../../styles/index.module.scss'
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { KizStateType, LoadingStatus } from "../../../@types/enumsGlobal";
import useGridFilter, { DefaultGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { KizMarkDataProvider } from "../../../Services/DataProviders/KizMarkDataProvider";
import { IKizMarkDTO, IKizMarkViewDTO } from "../../../libs/coreapi-dto/service/kizMark";
import { IOption } from "../../../components/selects/select";
import { DocumentSearchPanel } from "../../../components/commandsPanels/documentSearchPanel";
import { useTranslation } from "react-i18next";
import { ProgressActionSpinner } from "../../../components/progressActionSpinner/ProgressActionSpinner";
import { KizDocDataProvider } from "../../../Services/DataProviders/KizDocDataProvider";
import { ConfirmationItemsModal } from "../../../components/modalWindows/ConfirmationItemsModal";
import GridWrapper from "../../../components/controls/GridWrapper";
import stylesKizButton from '../../Accounting/CashSession/Components/KizButtonPanel.module.scss'
import { Button } from "../../../components/buttons/button";
import { Send10531, Send531 } from "../../../libs/corporate/icons/outlined/quickAccessBar/QuickAccessBarCollection";
import { KizDataProvider } from "../../../Services/DataProviders/KizDataProvider";
import { IKizDocBatchDisposalRequest, IKizDocBatchDisposalResult, IKizDTO } from "../../../libs/coreapi-dto/dirs/kiz";
import { promisifyMethod } from "../../../system/functions/promisifyMethod";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import { AdditionalButton } from "../../../components/buttons/iconButtons/other/PanelCollection";
import { EmptySquareButton } from "../../../components/buttons/iconButtons/editor/ActionCollection";
import Tooltip from "../../../components/tooltips/Tooltip";

const KizMarkGrid: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const kizMarkDataProvider = new KizMarkDataProvider(appContext.coreApiService);
    const kizDocDP = new KizDocDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);

    const { t } = useTranslation();

    const [data, setData] = useState<IKizMarkViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();

    const [selectedIdDocument, setSelectedIdDocument] = useState<string>("");

    const [searchArg, setSearchArg] = useState<{ query: string; option: string, initLoad: boolean }>({
        query: props.data?.query || '',
        option: props.data?.option || '',
        initLoad: props.data?.initLoad,
    });

    const [modalWindow, setModalWindow] = useState(<></>);
    const [multipleSelect, setMultipleSelect] = useState(false);
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);

    useEffect(() => {
        if (selectedItem && selectedItem.idGlobal.length > 0) {
            const idDocumentCell: IGridCell | undefined = selectedItem.cells.find(x => x.propertyName === "idDocumentGlobal");
            if (idDocumentCell) {
                setSelectedIdDocument(idDocumentCell.value);
            }
        } else {
            setSelectedIdDocument("");
        }
    }, [selectedItem])

    useEffect(() => {

        if (searchArg.initLoad && searchArg.query !== '') {
            props.data.initLoad = true;
            search(searchArg.option, searchArg.query);
        }
    }, [searchArg.initLoad])

    useEffect(() => {

        if (props.data.initLoad === false) {
            setSearchArg({...searchArg, initLoad: true})
            return;
        }

        setLoadingStatus(LoadingStatus.InProcess);
        kizMarkDataProvider.getView(gridFilter, (entities: IKizMarkDTO[], totalCount) => {
            try {
                setData(entities);
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
            } catch {
                setLoadingStatus(LoadingStatus.Failed);
            }
        })
    }, [gridFilter])

    const defaultSearchOptions = [
        {displayName: 'Номер документа', value: 'docNum'},
        {displayName: 'GTIN_SGTIN', value: 'gtinSgtin'},
    ] as IOption[];
        
    const search = (searchOption: string, searchStore: string) => {
        const setFilter = (option: string) => dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: `lower("${option}") like lower('%${searchStore}%')` });
        
        if (searchStore.length > 0) {
            switch(searchOption){
                case 'docNum' : {
                    return setFilter('DocNum');
                }
                case 'gtinSgtin' : {
                    return setFilter('GtinSgtin');
                }
            }
        } else {
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
        }
    };

    const [progressActionSpinnerJsx, setProgressActionSpinnerJsx] = useState<JSX.Element>(<></>);
        const manageProgressView = (actionMessage?: string, show = false) => {
            if (show) {
                setProgressActionSpinnerJsx(
                    <ProgressActionSpinner
                        loaderText={actionMessage}
                    />
                );
            } else {
                setProgressActionSpinnerJsx(<></>);
            }
        };

    // Повторная отправка (возможно вынести в отдельный файл)
    type RepeatSendMethod = 'createRepeatSend531Disposal' | 'createRepeatSendRvDisposal';

    async function createRepeatSendAsync(
        method: RepeatSendMethod,
        idGlobal: string,
        successMessageFull: string,
        successMessagePartial: string
    ) {
        manageProgressView(t("general.sendingRepeatDocument"), true);
        try {
            let result: boolean | null = false;

            await new Promise<string>((resolve, reject) => {
                kizDocDP[method](idGlobal, (e) => {
                    if (e.respType === 'isFailed') {
                        reject(e.message?.title);
                    } else {
                        result = e.data;
                        resolve(e.message?.text);
                    }
                });
            });

            const message = result ? successMessageFull : successMessagePartial;
            renderConfirmationModal('Успешно!', '', message, '', () => setModalWindow(<></>), () => setModalWindow(<></>));

        } catch (error) {
            if (typeof error === 'string') {
                renderConfirmationModal(t("general.problemSendingRepeatDocument"), '', error, '', () => setModalWindow(<></>), () => setModalWindow(<></>));
            }
        } finally {
            manageProgressView();
        }
    }

    async function createRepeatSend531DisposalAsync() {
        await createRepeatSendAsync(
            'createRepeatSend531Disposal',
            selectedIdDocument,
            t("general.sendingRepeatDocumentHasBeenSent"),
            t("general.sendingRepeatDocumentHasBeenSent")
        );
    }

    async function createRepeatSendRvDisposalAsync() {
        await createRepeatSendAsync(
            "createRepeatSendRvDisposal",
            selectedIdDocument,
            t("general.sendingRepeatDocumentHasBeenSent"),
            t("general.sendingRepeatDocumentHasBeenSent")
        );
    }

    const renderConfirmationModal = (header: string, warning: string, items: string, question: string, ok: () => void, cancel: () => void) => {
        setModalWindow(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={`${warning}`}
                listPositions={`${items}`}
                question={`${question}`}
                cancel={cancel}
                ok={ok}
                largeTextArea
            />
        );
    };

    // Очистка данных при смене груп. обработки.
    const handleGroupProcessing = () => {

        setMultipleSelect(prev => !prev);
        setSelectedItems([]);
        setSelectedItem(undefined);

        props?.onMultipleSelect?.([]);
        props.onSelect?.(undefined)
    }

    type ItemValidateResult = Readonly<{
        success: boolean;
        variant?: AlertVariant;
        message?: string;
        statusCode?: number;
    }>;

    function errorsAsStringWithDefaults(results: ItemValidateResult[]): string {
        return results
            .filter(result => !result.success)
            .map(result => {
                if (result.message) return result.message;

                // Дефолтные сообщения на основе статус-кода
                const defaultMessages: Record<number, string> = {
                    0: "",
                    400: "Некорректный запрос",
                    401: "Требуется авторизация",
                    403: "Доступ запрещен",
                    404: "Ресурс не найден",
                    500: "Внутренняя ошибка сервера"
                };

                if (result.statusCode && defaultMessages[result.statusCode]) {
                    return `${defaultMessages[result.statusCode]} (код: ${result.statusCode})`;
                }

                return result.statusCode
                    ? `Ошибка ${result.statusCode}`
                    : "Неизвестная ошибка";
            })
            .join('\n');
    }

    async function validateItemFor531Async(selectedItem: IGridRow | undefined): Promise<ItemValidateResult> {
        if (selectedItem && selectedItem.idGlobal.length > 0) {

            const gtinSgtinCell: IGridCell | undefined = selectedItem.cells.find(x => x.propertyName === "gtinSgtin");
            if (gtinSgtinCell) {
                const gtinSgtin: string = gtinSgtinCell.value;
                const kizDto: IKizDTO = await promisifyMethod<IKizDTO, typeof kizDP>(
                    kizDP,
                    kizDP.searchByGtinSgtin,
                    gtinSgtin
                );

                if (kizDto) {
                    // Если КИЗ выбыт, предупреждение
                    if (kizDto.state && kizDto.state === KizStateType.exit) {

                        return {
                            success: false,
                            variant: "info",
                            statusCode: 0,
                            message: `КИЗ '${kizDto.gtinSGtin}' выбыт`
                        } as const
                    }
                } else {
                    return {
                        success: false,
                        variant: "error",
                        statusCode: 404,
                        message: `КИЗ '${gtinSgtin}' не найден`
                    } as const;
                }

            } else {
                return {
                    success: false,
                    variant: "error",
                    statusCode: 0,
                    message: `Ошибка в табличных данных для марки ID = '${selectedItem.idGlobal}'`
                } as const
            }

            const pkuCell: IGridCell | undefined = selectedItem.cells.find(x => x.propertyName === "pku");
            if (pkuCell) {
                const pkuValue = pkuCell.value === true ||
                    (typeof pkuCell.value === 'string' && pkuCell.value.toLowerCase() === 'да');

                if (pkuValue) {
                    return {
                        success: false,
                        variant: "danger",
                        statusCode: 0,
                        message: "Запрещена отправка '531' схемы для товаров с признакам ПКУ, воспользуйтесь схемой '10531 отправка через РВ'"
                    } as const
                } else {
                    return {
                        success: true
                    } as const
                }
            } else {
                return {
                        success: false,
                        variant: "error",
                        statusCode: 0,
                        message: `Ошибка в табличных данных для марки ID = '${selectedItem.idGlobal}'`
                    } as const
            }
            
        } else {
            return {
                success: false,
                statusCode: 0,
                variant: "error",
                message: "Ошибка в табличных данных для марки",
            } as const
        }
    }

    async function validateItemFor10531Async(selectedItem: IGridRow | undefined): Promise<ItemValidateResult> {
        if (selectedItem && selectedItem.idGlobal.length > 0) {
            const gtinSgtinCell: IGridCell | undefined = selectedItem.cells.find(x => x.propertyName === "gtinSgtin");
            if (gtinSgtinCell) {
                const gtinSgtin: string = gtinSgtinCell.value;
                const kizDto: IKizDTO = await promisifyMethod<IKizDTO, typeof kizDP>(
                    kizDP,
                    kizDP.searchByGtinSgtin,
                    gtinSgtin
                );

                if (kizDto) {
                    // Если КИЗ выбыт, предупреждение
                    if (kizDto.state && kizDto.state === KizStateType.exit) {

                        return {
                            success: false,
                            variant: "info",
                            statusCode: 0,
                            message: `КИЗ '${kizDto.gtinSGtin}' выбыт`
                        } as const
                    }

                    const decodedBarcode: string = kizDto.decodedBarcode;
                    const rawDecodedBarcode: string = atob(kizDto.rawBarcode ?? "");

                    const isDecodedBarcodeInvalid: boolean = decodedBarcode.length < 32; // 32 длина без криптохвоста
                    const isRawDecodedBarcodeInvalid: boolean = rawDecodedBarcode.length < 32;

                    // Если не валидны сырой баркод и из БД - алерт ошибки 1
                    if (isRawDecodedBarcodeInvalid && isDecodedBarcodeInvalid) {

                        return {
                            success: false,
                            variant: "danger",
                            statusCode: 0,
                            message: `ШК КИЗ со сканера '${rawDecodedBarcode}' и системный '${decodedBarcode}' не корректны для выбытия`
                        } as const;
                    }

                    // Если сырой баркод не валидный, из БД ОК - алерт ошибки 2
                    if (isRawDecodedBarcodeInvalid && !isDecodedBarcodeInvalid) {

                        return {
                            success: false,
                            variant: "danger",
                            statusCode: 0,
                            message: `ШК КИЗ со сканера '${rawDecodedBarcode}' не корректен для выбытия, для данного КИЗ допустимо использовать системный ШК. Обратитесь к администратору`
                        } as const;
                    }

                    // Если сырой баркод ОК, но из БД не валидный - предупреждение 1 (true)
                    if (!isRawDecodedBarcodeInvalid && isDecodedBarcodeInvalid) {

                        return {
                            success: true,
                            variant: "info",
                            statusCode: 0,
                            message: `ШК КИЗ со сканера '${rawDecodedBarcode}' отличается от системного '${decodedBarcode}' - корректен для выбытия`
                        } as const;
                    }

                    return {
                        success: true
                    }

                } else {

                    return {
                        success: false,
                        variant: "error",
                        statusCode: 404,
                        message: `КИЗ '${gtinSgtin}' не найден`
                    } as const;
                }
            } else {
                
                return {
                    success: false,
                    variant: "error",
                    statusCode: 0,
                    message: `Ошибка в табличных данных для марки ID = '${selectedItem.idGlobal}'`
                } as const;
            }
        } else {

            return {
                success: false,
                statusCode: 0,
                variant: "error",
                message: "Ошибка в табличных данных для марки",
            } as const
        }
    }

    async function send531Async() {

        // Запрос для 1 марки
        if (!multipleSelect) {
            const validateResult = await validateItemFor531Async(selectedItem);
            if (validateResult.success) {
                if (validateResult.variant) {
                    renderGlobalAlert({
                        variant: validateResult.variant,
                        statusCode: validateResult.statusCode || 0,
                        title: validateResult.message || "",
                    });
                }

                // Отправка запроса 531
                await createRepeatSend531DisposalAsync();
            } else {
                renderGlobalAlert({
                    variant: validateResult.variant || "danger",
                    statusCode: validateResult.statusCode || 0,
                    title: validateResult.message || "",
                });
            }
        } else {

            const schemeMnemocode = "HealthCare";
            await batchRepeatAsync(schemeMnemocode);
        }
    }

    async function send10531Async() {

        // Запрос для 1 марки
        if (!multipleSelect) {
            const validateResult = await validateItemFor10531Async(selectedItem);
            if (validateResult.success) {
                if (validateResult.variant) {
                    renderGlobalAlert({
                        variant: validateResult.variant,
                        statusCode: validateResult.statusCode || 0,
                        title: validateResult.message || "",
                    });
                }

                // Отправка запроса 10531
                await createRepeatSendRvDisposalAsync();
            } else {
                renderGlobalAlert({
                    variant: validateResult.variant || "danger",
                    statusCode: validateResult.statusCode || 0,
                    title: validateResult.message || "",
                });
            }
        } else {

            const schemeMnemocode = "HealthCareDisposal";
            await batchRepeatAsync(schemeMnemocode);
        }
    }

    async function batchRepeatAsync(schemeMnemocode: string) {

        // Batch-запрос
        if (selectedItems.length > 0) {

            const alertResults: ItemValidateResult[] = [];
            const batchRequest: IKizDocBatchDisposalRequest = {
                documentIds: [],
                schemeMnemocode: schemeMnemocode
            };
            
            for (const item of selectedItems) {
                const docNumCell: IGridCell | undefined = item.cells.find(x => x.propertyName === "docNum");
                manageProgressView(`${t("general.validationRepeatDocument")}: ${docNumCell ? docNumCell.value : ""}`, true);

                const validateResult = await validateItemFor10531Async(item);
                if (!validateResult.success)
                    alertResults.push(validateResult);

                if (validateResult.success) {
                    const idDocumentCell: IGridCell | undefined = item.cells.find(x => x.propertyName === "idDocumentGlobal");
                    if (idDocumentCell)
                        batchRequest.documentIds.push(idDocumentCell.value);
                }
            };

            // Вывести модальное окно по ошибкам валидация
            const showAlerts = () => {
                if (alertResults.length > 0) {

                    setProgressActionSpinnerJsx(<></>);
                    renderConfirmationModal(
                        t("general.problemBatchRepeatDisposal"),
                        "",
                        errorsAsStringWithDefaults(alertResults),
                        '',
                        () => setModalWindow(<></>),
                        () => setModalWindow(<></>)
                    );
                }
            }

            if (batchRequest.documentIds.length > 0) {
                manageProgressView(`${t("general.prepareRepeatDisposal")} ${schemeMnemocode == "HealthCareDisposal" ? "10531" : "531"}`)

                const result = await promisifyMethod<IKizDocBatchDisposalResult, typeof kizDocDP>(
                    kizDocDP,
                    kizDocDP.createRepeatBatchDisposal,
                    batchRequest
                );

                console.info(result);

                if (result.success) {
                    setProgressActionSpinnerJsx(<></>);
                    setModalWindow(<></>);

                    renderGlobalAlert({
                        variant: "info",
                        statusCode: 0,
                        title: t("general.wellDoneBatchRepeatDisposal")
                    });

                    showAlerts();
                } else {
                    const alertMessages = alertResults.length > 0 ? errorsAsStringWithDefaults(alertResults) : "";

                    setProgressActionSpinnerJsx(<></>);
                    renderConfirmationModal(
                        t("general.problemBatchRepeatDisposal"),
                        "",
                        alertMessages.length > 0 ? alertMessages + '\n\n' + result.message : result.message,
                        '',
                        () => setModalWindow(<></>),
                        () => setModalWindow(<></>)
                    );
                }
            } else {
                setProgressActionSpinnerJsx(<></>);
                setModalWindow(<></>)

                showAlerts()
            }
        }
    }

    return (
        <>
            <PluginWrapper>
                <GridWrapper cols={4}>
                    <DocumentSearchPanel
                        onSubmit={(searchParams) => {
                            search(searchParams.searchOption, searchParams.searchStore);
                        }}
                        options={defaultSearchOptions}
                        query={searchArg?.query}
                    />

                    <BaseCommandsPanel groups={
                        [
                            [
                                {
                                    iconButton: <Tooltip title="Групповая обработка">
                                        {multipleSelect ? (
                                            <AdditionalButton onClick={handleGroupProcessing} sizeVariant="mini" disabled={data.length === 0} />
                                        ) : (
                                            <EmptySquareButton onClick={handleGroupProcessing} sizeVariant="mini" disabled={data.length === 0} />
                                        )}
                                    </Tooltip>
                                }
                            ]
                        ]
                    }>
                    </BaseCommandsPanel>

                </GridWrapper>

                <GridWrapper cols={5}>
                    <Button
                        className={styles.kizButton}
                        variant={"kizPanel"}
                        onClick={async () => await send10531Async()}
                        disabled={selectedIdDocument.length === 0 && selectedItems.length === 0}
                    >
                        <Send10531 className={stylesKizButton.kizIcon} />
                        {t("directory.disposal.repeat10531")}
                    </Button>
                    <Button
                        className={styles.kizButton}
                        variant={"kizPanel"}
                        onClick={async () => await send531Async()}
                        disabled={selectedIdDocument.length === 0 && selectedItems.length === 0}
                    >
                        <Send531 className={stylesKizButton.kizIcon} />
                        {t("directory.disposal.repeat531")}
                    </Button>
                </GridWrapper>
            
                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        <DefaultGrid
                            showQuantity
                            gridId={props.gridId}
                            data={data}
                            loadingStatus={loadingStatus}
                            setLoadingStatus={setLoadingStatus}
                            multipleSelect={multipleSelect}
                            totalCount={totalCount}
                            dataProvider={kizMarkDataProvider}
                            filter={gridFilter}
                            separator
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            selectedItems={selectedItems}
                            onSelect={(row) => {
                                setSelectedItem(row)
                                props?.onSelect?.(row)
                            }}
                            onMultipleSelect={(rows) => {
                                setSelectedItems(rows);
                                props?.onMultipleSelect?.(rows)
                            }}
                            onDoubleClick={(i) => props.onDoubleClick?.(i)}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            hiddenPagination={{ hiddenCountRow: true, hiddenNumberPage: true }}
                            searching
                        />
                    </div>
                    {modalWindow}
                    {progressActionSpinnerJsx}
                </div>
            </PluginWrapper>
        </>
    )
}

export default KizMarkGrid